import { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import { PaginationBar } from "@components/shared/PaginationBar/PaginationBar";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwButton from "@components/shared/OwButton/OwButton";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import { useAppContextNonNull } from "@contexts/AppContext";
import FastBoundService from "@services/FastBoundService";
import CatalogService from "@services/CatalogService";
import CatalogItemService from "@services/CatalogItemService";
import ISerial from "@models/ISerial";
import IOwTableCols from "@models/IOwTableCols";
import SortOrderEnum from "@models/SortOrderEnum";
import IBreadcrumbs from "@models/IBreadcrumbs";
import ICatalog from "@models/ICatalog";
import ICatalog_Item from "@models/ICatalog_Item";
import IFastbound_Account from "@models/IFastbound_Account";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";

type Props = {};

const STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "disposed", label: "Disposed" },
    { value: "deleted", label: "Deleted" },
    { value: "theft_loss", label: "Theft / Loss" },
    { value: "destroyed", label: "Destroyed" },
    { value: "dismissed", label: "Dismissed" },
];

const getStatusBadge = (status: string | null | undefined) => {
    const s = status || "active";
    switch (s) {
        case "active":
            return <OwBadge title="active" bgColor="bg-success" skipTranslate />;
        case "disposed":
            return <OwBadge title="disposed" bgColor="bg-warning" textColor="#000" skipTranslate />;
        case "deleted":
            return <OwBadge title="deleted" bgColor="bg-danger" skipTranslate />;
        case "theft_loss":
            return <OwBadge title="theft / loss" bgColor="bg-danger" skipTranslate />;
        case "destroyed":
            return <OwBadge title="destroyed" bgColor="bg-danger" skipTranslate />;
        case "dismissed":
            return <OwBadge title="dismissed" bgColor="bg-secondary" skipTranslate />;
        default:
            return <OwBadge title={s} bgColor="bg-secondary" skipTranslate />;
    }
};

const getModalStyle = (small = false) => ({
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        transform: "translate(-50%, -50%)",
        width: small ? "380px" : "580px",
        maxWidth: "95vw",
        overflow: "visible",
        color: "var(--main-text-color)",
        backgroundColor: "var(--main-bg-color)",
        border: "1px solid var(--minor-bg-color)",
    },
    overlay: {
        zIndex: 1050,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
});

export const FastBoundPendingSerials = (_props: Props) => {
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();

    const crumbs: IBreadcrumbs[] = [
        { title: "business_integrations", url: "/business/integrations" },
        { title: "fastbound_dashboard", url: "/business/integrations/fastbound/dashboard" },
        { title: "fastbound_pending_serials", url: "/business/integrations/fastbound/pending_serials" },
    ];

    // Data
    const [serials, setSerials] = useState<ISerial[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isDataFetching, setIsDataFetching] = useState<boolean>(true);
    const [isInitialData, setIsInitialData] = useState<boolean>(true);

    // Accounts for filter dropdown
    const [accounts, setAccounts] = useState<IFastbound_Account[]>([]);

    // Filter state
    const [serialSearch, setSerialSearch] = useState<string>("");
    const [manufacturer, setManufacturer] = useState<string>("");
    const [model, setModel] = useState<string>("");
    const [caliber, setCaliber] = useState<string>("");
    const [filterType, setFilterType] = useState<string>("");
    const [selectedAccount, setSelectedAccount] = useState<IFastbound_Account | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>("");

    // Sort
    const [sortCol, setSortCol] = useState<string>("created_at");
    const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.DESC);

    // Pagination
    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(25);
    const [hasPaginationChange, setHasPaginationChange] = useState<boolean>(false);

    // Row selection
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    // Assign modal
    const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
    const [assignTarget, setAssignTarget] = useState<ISerial | null>(null);
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [assignCatalog, setAssignCatalog] = useState<ICatalog | null>(null);
    const [catalogItems, setCatalogItems] = useState<ICatalog_Item[]>([]);
    const [assignCatalogItem, setAssignCatalogItem] = useState<ICatalog_Item | null>(null);
    const [resetTypeahead, setResetTypeahead] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Dismiss/Restore modal
    const [showDismissModal, setShowDismissModal] = useState<boolean>(false);

    const fetchSerials = useCallback(async () => {
        try {
            setIsDataFetching(true);
            const page = Math.floor(offset / limit);
            const query: Record<string, any> = {
                page,
                limit,
                order_by: sortCol,
                order_dir: sortOrder.toLowerCase(),
            };
            if (serialSearch) query.serial = serialSearch;
            if (manufacturer) query.manufacturer = manufacturer;
            if (model) query.model = model;
            if (caliber) query.caliber = caliber;
            if (filterType) query.type = filterType;
            if (selectedAccount) query.fastbound_account_id = selectedAccount.id;
            if (statusFilter) query.fastbound_status = statusFilter;

            const data = await FastBoundService.getPendingSerials(query, userModel, supplierModel);
            setSerials(data.rows || []);
            setTotalCount(data.count || 0);
        } catch (error) {
            console.error("Error fetching pending serials:", error);
            setSerials([]);
            setTotalCount(0);
        } finally {
            setIsDataFetching(false);
            setIsInitialData(false);
            setHasPaginationChange(false);
        }
    }, [
        userModel,
        supplierModel,
        offset,
        limit,
        sortCol,
        sortOrder,
        serialSearch,
        manufacturer,
        model,
        caliber,
        filterType,
        selectedAccount,
        statusFilter,
    ]);

    const fetchAccounts = useCallback(async () => {
        try {
            const data = await FastBoundService.getAccounts(userModel, supplierModel);
            setAccounts(data || []);
        } catch (error) {
            console.error("Error fetching FastBound accounts:", error);
        }
    }, [userModel, supplierModel]);

    const fetchCatalogs = useCallback(async () => {
        try {
            const data = await CatalogService.getCatalogs(userModel, supplierModel, vendorModel);
            setCatalogs(data.catalogs || []);
        } catch (error) {
            console.error("Error fetching catalogs:", error);
        }
    }, [userModel, supplierModel, vendorModel]);

    useEffect(() => {
        window.document.title = "Orderwerks >> FastBound Pending Serials";
        fetchSerials();
        fetchAccounts();
        fetchCatalogs();
    }, []);

    useEffect(() => {
        if (hasPaginationChange) {
            fetchSerials();
        }
    }, [hasPaginationChange]);

    useEffect(() => {
        fetchSerials();
    }, [sortCol, sortOrder]);

    const performSearch = () => {
        setOffset(0);
        fetchSerials();
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter") performSearch();
    };

    const resetFilters = () => {
        setSerialSearch("");
        setManufacturer("");
        setModel("");
        setCaliber("");
        setFilterType("");
        setSelectedAccount(null);
        setStatusFilter("");
        setOffset(0);
        setLimit(25);
        setSelectedIds(new Set());
        fetchSerials();
    };

    const toggleSortDir = (_col: string, _order: SortOrderEnum) => {
        setSortCol(_col);
        setSortOrder(_order);
    };

    const paginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
        setHasPaginationChange(true);
    };

    const toggleRow = (id: number, checked: boolean) => {
        const next = new Set(selectedIds);
        if (checked) next.add(id);
        else next.delete(id);
        setSelectedIds(next);
    };

    const selectAllOnPage = () => {
        const next = new Set(selectedIds);
        serials.forEach((s) => next.add(s.id));
        setSelectedIds(next);
    };

    const clearSelection = () => setSelectedIds(new Set());

    // Assign modal helpers
    const openAssignModal = (target: ISerial | null) => {
        setAssignTarget(target);
        setAssignCatalog(null);
        setAssignCatalogItem(null);
        setCatalogItems([]);
        setResetTypeahead(true);
        setShowAssignModal(true);
    };

    const handleAssignCatalogSelect = (catalog: ICatalog | null) => {
        setAssignCatalog(catalog);
        setAssignCatalogItem(null);
        setCatalogItems([]);
        setResetTypeahead(true);
    };

    const doTypeaheadCatalogItem = async (searchPhrase: string) => {
        if (!searchPhrase || !assignCatalog) return;
        try {
            const data = await CatalogItemService.typeaheadCatalogItems(
                assignCatalog.guid,
                searchPhrase,
                userModel,
                supplierModel,
                vendorModel
            );
            setCatalogItems(data.catalog_items || []);
        } catch (error) {
            console.error("Error searching catalog items:", error);
        }
    };

    const confirmAssign = async () => {
        if (!assignCatalog || !assignCatalogItem) return;
        setIsSubmitting(true);
        try {
            if (assignTarget) {
                await FastBoundService.assignSerial(
                    assignTarget.id,
                    { catalogId: assignCatalog.id, catalogItemId: assignCatalogItem.id },
                    userModel,
                    supplierModel
                );
                uiContext.notifyFn(null, {
                    message: "Serial assigned successfully",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                });
            } else {
                const result = await FastBoundService.bulkAssignSerials(
                    { serialIds: Array.from(selectedIds), catalogId: assignCatalog.id, catalogItemId: assignCatalogItem.id },
                    userModel,
                    supplierModel
                );
                uiContext.notifyFn(null, {
                    message: `Assigned ${result.assigned ?? 0} serial(s)`,
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                });
            }
            setShowAssignModal(false);
            setSelectedIds(new Set());
            fetchSerials();
        } catch (error: any) {
            uiContext.notifyFn(null, {
                message: error.message || "Failed to assign serials",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDismiss = async () => {
        setIsSubmitting(true);
        try {
            const ids = Array.from(selectedIds);
            if (statusFilter === "dismissed") {
                const result = await FastBoundService.undismissSerials(ids, userModel, supplierModel);
                uiContext.notifyFn(null, {
                    message: `Restored ${result.restored ?? 0} serial(s)`,
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                });
            } else {
                const result = await FastBoundService.dismissSerials(ids, userModel, supplierModel);
                uiContext.notifyFn(null, {
                    message: `Dismissed ${result.dismissed ?? 0} serial(s)`,
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                });
            }
            setShowDismissModal(false);
            setSelectedIds(new Set());
            fetchSerials();
        } catch (error: any) {
            uiContext.notifyFn(null, {
                message: error.message || "Operation failed",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (date: string | Date) => {
        if (!date) return "";
        return new Date(date).toLocaleDateString();
    };

    const isDismissMode = statusFilter === "dismissed";
    const assignCount = assignTarget ? 1 : selectedIds.size;

    const columns: IOwTableCols[] = [
        {
            title: "",
            valueProp: "id",
            renderFn: (row: ISerial) => (
                <input
                    type="checkbox"
                    className="form-check-input pointer"
                    checked={selectedIds.has(row.id)}
                    onChange={(e) => toggleRow(row.id, e.target.checked)}
                />
            ),
            renderFnEmitsRow: true,
        },
        {
            title: "serial",
            valueProp: "value",
            isSortable: true,
            sortCol: "value",
            sortOrderFn: () => sortOrder,
            sortFn: (_col: string, _order: SortOrderEnum) => toggleSortDir("value", _order),
        },
        {
            title: "manufacturer",
            valueProp: "fastbound_item_metadata",
            renderFn: (row: ISerial) => row.fastbound_item_metadata?.manufacturer || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "model",
            valueProp: "fastbound_item_metadata",
            renderFn: (row: ISerial) => row.fastbound_item_metadata?.model || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "caliber",
            valueProp: "fastbound_item_metadata",
            renderFn: (row: ISerial) => row.fastbound_item_metadata?.caliber || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "type",
            valueProp: "fastbound_item_metadata",
            renderFn: (row: ISerial) => row.fastbound_item_metadata?.type || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "sku",
            valueProp: "fastbound_item_metadata",
            renderFn: (row: ISerial) => row.fastbound_item_metadata?.sku || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "status",
            valueProp: "fastbound_status",
            renderFn: (row: ISerial) => getStatusBadge(row.fastbound_status),
            renderFnEmitsRow: true,
        },
        {
            title: "account",
            valueProp: "fastbound_account_id",
            renderFn: (row: any) => row.Fastbound_Account?.name || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "date_synced",
            valueProp: "created_at",
            renderFn: (row: ISerial) => formatDate(row.created_at),
            renderFnEmitsRow: true,
            isSortable: true,
            sortCol: "created_at",
            sortOrderFn: () => sortOrder,
            sortFn: (_col: string, _order: SortOrderEnum) => toggleSortDir("created_at", _order),
        },
        {
            title: "actions",
            valueProp: "id",
            renderFn: (row: ISerial) => <OwButton title="assign" fn={() => openAssignModal(row)} className="btn-primary btn-sm" />,
            renderFnEmitsRow: true,
        },
    ];

    return (
        <div className="FastBoundPendingSerials pb-5 pb-md-0">
            <Breadcrumbs crumbs={crumbs} />
            <OwTitle title="fastbound_pending_serials" leftIconName="fad fa-list" tag="h3" />

            <div className="mb-2">
                <SearchSection>
                    <div className="row mb-2 align-items-end">
                        <div className="mb-2 mb-md-0 col-12 col-sm-6 col-md-2">
                            <OwLabel title="serial" />
                            <OwFnInput
                                fn={(e: any) => setSerialSearch(e.target.value)}
                                name="serialSearch"
                                value={serialSearch}
                                onKeyDownFn={enterSearch}
                            />
                        </div>
                        <div className="mb-2 mb-md-0 col-12 col-sm-6 col-md-2">
                            <OwLabel title="manufacturer" />
                            <OwFnInput
                                fn={(e: any) => setManufacturer(e.target.value)}
                                name="manufacturer"
                                value={manufacturer}
                                onKeyDownFn={enterSearch}
                            />
                        </div>
                        <div className="mb-2 mb-md-0 col-12 col-sm-6 col-md-2">
                            <OwLabel title="model" />
                            <OwFnInput fn={(e: any) => setModel(e.target.value)} name="model" value={model} onKeyDownFn={enterSearch} />
                        </div>
                        <div className="mb-2 mb-md-0 col-12 col-sm-6 col-md-2">
                            <OwLabel title="caliber" />
                            <OwFnInput
                                fn={(e: any) => setCaliber(e.target.value)}
                                name="caliber"
                                value={caliber}
                                onKeyDownFn={enterSearch}
                            />
                        </div>
                        <div className="mb-2 mb-md-0 col-12 col-sm-6 col-md-2">
                            <OwLabel title="type" />
                            <OwFnInput
                                fn={(e: any) => setFilterType(e.target.value)}
                                name="type"
                                value={filterType}
                                onKeyDownFn={enterSearch}
                            />
                        </div>
                        <div className="mb-2 mb-md-0 col-12 col-sm-6 col-md-2">
                            <OwLabel title="account" />
                            <OwSelectOption
                                options={accounts}
                                valueProp="id"
                                titleProp="name"
                                fn={setSelectedAccount}
                                name="account"
                                showBlankFirst={true}
                                selectedOption={selectedAccount}
                                selectedOptionProp="id"
                                emitObject={true}
                                sizeClass="form-select-sm"
                            />
                        </div>
                        <div className="mb-2 mb-md-0 col-12 col-sm-6 col-md-2">
                            <OwLabel title="status" />
                            <OwSelectOption
                                options={STATUS_OPTIONS}
                                valueProp="value"
                                titleProp="label"
                                fn={(opt: any) => setStatusFilter(opt ? opt.value : "")}
                                name="status"
                                showBlankFirst={true}
                                selectedOption={STATUS_OPTIONS.find((o) => o.value === statusFilter) || null}
                                selectedOptionProp="value"
                                emitObject={true}
                                sizeClass="form-select-sm"
                            />
                        </div>
                        <div className="col-auto">
                            <OwButton title="search" fn={performSearch} className="btn-primary me-2" />
                            <OwButton title="reset" fn={resetFilters} className="btn-danger" />
                        </div>
                    </div>
                </SearchSection>
            </div>

            {selectedIds.size > 0 ? (
                <div className="d-flex align-items-center flex-wrap gap-2 mb-2 p-2 rounded secondary-bg-color">
                    <span className="me-1 fw-bold">{selectedIds.size} selected</span>
                    <OwButton title="assign_to_catalog_item" fn={() => openAssignModal(null)} className="btn-primary btn-sm" />
                    <OwButton
                        title={isDismissMode ? "restore" : "dismiss"}
                        fn={() => setShowDismissModal(true)}
                        className={`btn-sm ${isDismissMode ? "btn-success" : "btn-warning"}`}
                    />
                    <OwButton title="select_all_on_page" fn={selectAllOnPage} className="btn-secondary btn-sm" />
                    <OwButton title="clear_selection" fn={clearSelection} className="btn-outline-secondary btn-sm" />
                </div>
            ) : (
                serials.length > 0 && (
                    <div className="mb-2">
                        <OwButton title="select_all_on_page" fn={selectAllOnPage} className="btn-secondary btn-sm" />
                    </div>
                )
            )}

            <div className="table-responsive position-relative">
                <OwFnTable
                    id="pending-serials-table"
                    columns={columns}
                    dataSet={serials}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                    currentSortCol={sortCol}
                    wrappingClassName="w-100"
                />
            </div>

            {serials.length > 0 && (
                <PaginationBar offset={offset} limit={limit} totalRecords={totalCount} onPaginationChanged={paginationChanged} />
            )}

            {/* Assign Modal */}
            <Modal
                isOpen={showAssignModal}
                onRequestClose={() => setShowAssignModal(false)}
                contentLabel="Assign Serial"
                shouldCloseOnOverlayClick={false}
                style={getModalStyle()}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <strong>
                        Assign {assignCount} Serial{assignCount !== 1 ? "s" : ""} to Catalog Item
                    </strong>
                    <OwButton
                        className="btn-danger"
                        centerIconClass="fas fa-times"
                        fn={() => setShowAssignModal(false)}
                        disabled={isSubmitting}
                    />
                </div>
                <hr />
                <div className="mb-3">
                    <OwLabel title="catalog" />
                    <OwSelectOption
                        options={catalogs}
                        valueProp="id"
                        titleProp="name"
                        fn={handleAssignCatalogSelect}
                        name="assignCatalog"
                        showBlankFirst={true}
                        selectedOption={assignCatalog}
                        selectedOptionProp="id"
                        emitObject={true}
                    />
                </div>
                <div className="mb-3">
                    <OwLabel title="catalog_item" />
                    <TypeaheadFn
                        lookupFn={doTypeaheadCatalogItem}
                        availableOptions={catalogItems}
                        selectedOption={assignCatalogItem}
                        selectedOptionFn={setAssignCatalogItem}
                        titleProp="sku"
                        subTitleProp="name"
                        placeholder="Search by SKU or Name"
                        disabled={!assignCatalog}
                        resetTypeahead={resetTypeahead}
                        resetTypeaheadCallbackFn={() => setResetTypeahead(false)}
                        clearSelectionFn={() => setAssignCatalogItem(null)}
                        emitSelectionSetup={{ emitObject: true }}
                        isScrollable={true}
                        maxHeight={200}
                    />
                </div>
                <hr />
                <div className="d-flex justify-content-end">
                    <OwButton
                        title="confirm"
                        fn={confirmAssign}
                        className="btn-primary me-2"
                        disabled={!assignCatalog || !assignCatalogItem || isSubmitting}
                    />
                    <OwButton title="cancel" fn={() => setShowAssignModal(false)} className="btn-secondary" disabled={isSubmitting} />
                </div>
            </Modal>

            {/* Dismiss / Restore Confirm Modal */}
            <Modal
                isOpen={showDismissModal}
                onRequestClose={() => setShowDismissModal(false)}
                contentLabel="Dismiss Serials"
                shouldCloseOnOverlayClick={false}
                style={getModalStyle(true)}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <strong>{isDismissMode ? "Restore Serials" : "Dismiss Serials"}</strong>
                    <button type="button" className="btn-close" onClick={() => setShowDismissModal(false)} disabled={isSubmitting} />
                </div>
                <hr />
                <p>
                    {isDismissMode
                        ? `Restore ${selectedIds.size} serial${selectedIds.size !== 1 ? "s" : ""}?`
                        : `Dismiss ${selectedIds.size} serial${selectedIds.size !== 1 ? "s" : ""}?`}
                </p>
                <hr />
                <div className="d-flex justify-content-end">
                    <OwButton
                        title="confirm"
                        fn={confirmDismiss}
                        className={`me-2 ${isDismissMode ? "btn-success" : "btn-warning"}`}
                        disabled={isSubmitting}
                    />
                    <OwButton title="cancel" fn={() => setShowDismissModal(false)} className="btn-secondary" disabled={isSubmitting} />
                </div>
            </Modal>
        </div>
    );
};
